import React, { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogDescription,
} from '../../../../../components/ui/Dialog';
import Button from '../../../../../components/ui/Button';
import AppIcon from '../../../../../components/AppIcon';
import { reopenSettlement } from '../../../../../api/Payroll/payroll';

type Props = Readonly<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  onSuccess: () => void;
}>;

const ReopenSettlementDialog: React.FC<Props> = ({ open, onOpenChange, userId, onSuccess }) => {
  const { t } = useTranslation('common');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setReason('');
      setSubmitting(false);
      setError('');
    }
  }, [open]);

  const dismissable = !submitting;
  const reasonValid = reason.trim().length > 0;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!reasonValid) {
      setError(t('A reason is required to reopen a confirmed settlement.'));
      return;
    }
    setSubmitting(true);
    try {
      const result = await reopenSettlement({ userId, reason: reason.trim() });
      if (result.errors?.length) {
        setError(result.errors[0]?.description ?? t('Failed to reopen settlement'));
        return;
      }
      onSuccess();
      onOpenChange(false);
    } catch {
      setError(t('An unexpected error occurred'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !dismissable) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        size="sm"
        hideCloseButton={!dismissable}
        onEscapeKeyDown={(e) => { if (!dismissable) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (!dismissable) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{t('Reopen this settlement?')}</DialogTitle>
          <DialogDescription>
            {t('Reopening reverts the confirmation back to a draft so the settlement can be edited and re-confirmed. This action is recorded in the audit trail.')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <DialogBody className="space-y-4">
            <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              <AppIcon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
              <p>{t('The current confirmation will be cleared. Any linked payroll entries should be reviewed.')}</p>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="settlement-reopen-reason" className="text-sm font-medium text-foreground">
                {t('Reason for reopening')} <span className="text-destructive">*</span>
              </label>
              <textarea
                id="settlement-reopen-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                autoFocus
                disabled={submitting}
                maxLength={500}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:opacity-50"
              />
            </div>

            {error && (
              <p className="text-sm text-destructive" role="alert">{error}</p>
            )}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
              {t('Cancel')}
            </Button>
            <Button type="submit" variant="destructive" loading={submitting} disabled={submitting || !reasonValid}>
              {t('Reopen settlement')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReopenSettlementDialog;
